import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
    border: "solid 1px #fff",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "0px 8px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  height: "40px",
}));

export default function TableSingle({
  filterValue,
  formBody,
  setFormBody,
  currentPage,
  setCurrentPage,
  selectedRetailer,
  fuzzyIsActive,
  enableFuzzyMatchingFeature
}) {
  const accessToken = Cookies.get("access_token") || "";

  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const itemsPerPageList = [5, 10, 50, 100];
  const [totalItems, setTotalItems] = useState("");

  const [dataTable, setDataTable] = useState([]);
  const hasSimilarityScore = dataTable.some(
    (row) => row.similarity_score !== undefined
  );

  async function fetchMainData() {
    setDataTable([]);
    setIsLoading(true);

    const queryParams = new URLSearchParams();

    queryParams.append("page", currentPage);
    queryParams.append("size", itemsPerPage);

    if (enableFuzzyMatchingFeature) {
      if (fuzzyIsActive == true) {
        queryParams.append("need_regex_fuzzy", "true");
      } else if (fuzzyIsActive == false) {
        queryParams.append("need_regex_fuzzy", "false")
      }
    } else {
      if (filterValue) {
        queryParams.append("column_name", "product_name");
        queryParams.append("column_operator", "LIKE");
        queryParams.append("column_value", filterValue);
      }
    }

    if (formBody.multiple_selection.length > 0) {
      const latestItem =
        formBody.multiple_selection[formBody.multiple_selection.length - 1];

      queryParams.append("brand_master_id", latestItem.idbrand);
      queryParams.append("category_master_id", latestItem.idcategory);
    }

    queryParams.append("search_term", filterValue);

    const queryString = queryParams.toString();

    let apiUrl = "";

    if (enableFuzzyMatchingFeature) {
      apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/data-processing-raw-crosscoding/rta_mt/master/product` +
        (queryString ? `?${queryString}` : "");
    } else {
      apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/product-master` +
        (queryString ? `?${queryString}` : "");
    }

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTotalItems(data.pagination_info.total);
        setDataTable(data.data);

        const highestScoringRow = data.data.reduce((maxRow, currentRow) => {
          if (
            currentRow.similarity_score >
            (maxRow ? maxRow.similarity_score : 80)
          ) {
            return currentRow;
          }
          return maxRow;
        }, null);

        if (highestScoringRow) {
          setFormBody((prev) => ({
            ...prev,
            target_update: {
              idproduct: highestScoringRow.product_id,
            },
          }));
        }
      } else {
        setDataTable([]);
      }
    } catch (error) {
      setDataTable([]);
    }

    setIsLoading(false);
  }

  const handleChange = (row) => () => {
    setFormBody((prev) => ({
      ...prev,
      target_update: {
        idproduct: row.product_id,
        // idbrand: row.idbrand,
      },
    }));
  };

  // pagination
  const handlePageChange = (event, page) => {
    setCurrentPage(page + 1);
  };

  const handleChangeItemPerPageSelect = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedRetailer) {
        fetchMainData();
      }
    }, 700);
    return () => {
      clearTimeout(handler);
    };
  }, [
    accessToken,
    // selectedRetailer,
    filterValue,
    currentPage,
    itemsPerPage,
    formBody.multiple_selection,
    fuzzyIsActive,
  ]);

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="checkbox table">
          <TableHead>
            <TableRow sx={{ height: "10px" }}>
              <StyledTableCell>Select</StyledTableCell>
              <StyledTableCell align="left">CODE</StyledTableCell>
              <StyledTableCell align="left">PRODUCT</StyledTableCell>
              {hasSimilarityScore && (
                <StyledTableCell align="left">SIMILARITY SCORE</StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataTable.length > 0 ? (
              dataTable.map((row) => (
                <StyledTableRow key={`${row.id}`}>
                  <StyledTableCell
                    sx={{
                      width: "10%",
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                    align="center"
                  >
                    <Checkbox
                      checked={
                        // formBody.target_update.idcategory === row.idcategory &&
                        formBody.target_update.idproduct === row.product_id
                      }
                      sx={{ padding: "0px" }}
                      onChange={handleChange(row)}
                      color="primary"
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    align="left"
                    sx={{
                      width: `${hasSimilarityScore ? "30%" : "45%"}`,
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.product_id}
                  </StyledTableCell>

                  <StyledTableCell
                    align="left"
                    sx={{
                      width: `${hasSimilarityScore ? "30%" : "45%"}`,
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.product_name}
                  </StyledTableCell>
                  {row.similarity_score && (
                    <StyledTableCell
                      align="left"
                      sx={{
                        width: `${hasSimilarityScore ? "30%" : "0%"}`,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                      }}
                    >
                      {row.similarity_score.toFixed(1)}%
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ border: 1, borderColor: "grey.300" }}
                >
                  {isLoading ? "Loading..." : "No Data Available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {dataTable.length > 0 && (
        <TablePagination
          rowsPerPageOptions={itemsPerPageList}
          component="div"
          count={totalItems}
          rowsPerPage={itemsPerPage}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeItemPerPageSelect}
        />
      )}
    </>
  );
}
